import React, { useState, useEffect, useMemo } from 'react';

export const CVNav = () => {
  const [activeSection, setActiveSection] = useState('experience');
  const [hoveredItem, setHoveredItem] = useState(null);

  const navItems = useMemo(() => [
    { id: 'experience', label: 'EXPERIENCE' },
    { id: 'education', label: 'EDUCATION' },
    { id: 'workshops', label: 'WORKSHOPS' },
    { id: 'portfolio', label: 'PORTFOLIO' },
    { id: 'skills', label: 'SKILLS' },
  ], []);

  useEffect(() => {
    const handleScroll = () => {
      // Get all section elements
      const sections = [
        document.querySelector('section[aria-label="Professional Experience"]'),
        document.querySelector('section[aria-label="Education"]'),
        document.querySelector('section[aria-label="Workshops and Events"]'),
        document.querySelector('section[aria-label="Portfolio Projects"]'),
        document.querySelector('section[aria-label="Hard Skills"]'),
      ];

      // Find the active section based on which section is currently in view
      let newActiveSection = 'experience'; // default

      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        if (section) {
          const rect = section.getBoundingClientRect();
          // Check if section is in viewport
          if (rect.top <= 300 && rect.bottom >= 300) {
            newActiveSection = navItems[i].id;
            break;
          }
        }
      }

      setActiveSection(newActiveSection);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navItems]);

  const scrollToSection = (sectionId) => {
    const sectionMap = {
      'experience': 'Professional Experience',
      'education': 'Education',
      'workshops': 'Workshops and Events',
      'portfolio': 'Portfolio Projects',
      'skills': 'Hard Skills',
    };

    const section = document.querySelector(`section[aria-label="${sectionMap[sectionId]}"]`);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <nav className="fixed left-0 top-0 bottom-0 z-50 hidden md:flex items-center no-print">
      <div className="relative h-full flex items-center pl-6">
        {/* Vertical line */}
        <div className="absolute left-4 top-1/2 -translate-y-1/2 w-[2px] h-[70%] bg-charcoal/20"></div>

        {/* Nav items */}
        <div className="relative flex flex-col justify-center gap-12">
          {navItems.map((item) => {
            const isActive = activeSection === item.id;
            const isHovered = hoveredItem === item.id;
            const color = isActive ? '#ff6347' : '#1a1818';

            return (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                onMouseEnter={() => setHoveredItem(item.id)}
                onMouseLeave={() => setHoveredItem(null)}
                className="group relative flex items-center cursor-pointer"
                aria-label={`Navigate to ${item.label} section`}
              >
                {/* Dash + Dot indicator */}
                <div className="mr-2 flex items-center gap-1">
                  {/* Dash */}
                  <div
                    className="transition-all duration-300 ease-out"
                    style={{
                      width: isActive ? '32px' : isHovered ? '20px' : '12px',
                      height: '2px',
                      backgroundColor: color,
                      opacity: isActive ? 1 : isHovered ? 0.8 : 0.5
                    }}
                  ></div>
                  {/* Dot */}
                  <div
                    className="rounded-full transition-all duration-300 ease-out"
                    style={{
                      width: isActive ? '6px' : '4px',
                      height: isActive ? '6px' : '4px',
                      backgroundColor: color,
                      opacity: isActive ? 1 : isHovered ? 0.9 : 0.7
                    }}
                  ></div>
                </div>

                {/* Vertical text */}
                <div className="relative overflow-hidden" style={{ width: '14px' }}>
                  <div
                    className="font-[550] tracking-[4px] transition-all duration-500 ease-out"
                    style={{
                      writingMode: 'vertical-rl',
                      transform: `rotate(180deg) translateX(${isActive || isHovered ? '0px' : '-100%'})`,
                      fontSize: '10px',
                      color: color,
                      opacity: isActive ? 1 : isHovered ? 0.8 : 0
                    }}
                  >
                    {item.label}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};
